import { keepPreviousData, useInfiniteQuery, useQuery, type InfiniteData, type UseInfiniteQueryResult, type UseQueryResult } from '@tanstack/react-query';
import { queryKeys } from '../../queryKeys.ts';
import type { RisksTableListParams } from '../../queries/risks/keys.ts';
import {
  fetchRiskControlFilterOptionsPage,
  fetchRiskProcessFilterOptionsPage,
  fetchRisksDashboardMetrics,
  fetchRisksTablePage,
} from '../../request/risks/functional.js';

export type RisksTableListView = Awaited<ReturnType<typeof fetchRisksTablePage>>;
export type RiskProcessFilterOptionsPageView = Awaited<ReturnType<typeof fetchRiskProcessFilterOptionsPage>>;
export type RiskControlFilterOptionsPageView = Awaited<ReturnType<typeof fetchRiskControlFilterOptionsPage>>;
export type RisksDashboardMetricsView = Awaited<ReturnType<typeof fetchRisksDashboardMetrics>>;

export function useRisksTableList(params: RisksTableListParams): UseQueryResult<RisksTableListView, Error> {
  return useQuery({
    queryKey: queryKeys.risks.list(params),
    queryFn: () => fetchRisksTablePage(params),
    placeholderData: keepPreviousData,
  });
}

export function useRiskProcessFilterOptions(
  search: string,
  riskId = '',
  enabled = true,
  soxCycleCode = ''
): UseInfiniteQueryResult<InfiniteData<RiskProcessFilterOptionsPageView>, Error> {
  const trimmedRiskId = String(riskId ?? '').trim();
  const trimmedCycleCode = String(soxCycleCode ?? '').trim();
  return useInfiniteQuery({
    queryKey: [...queryKeys.risks.processFilterOptions(), trimmedRiskId, trimmedCycleCode, search],
    enabled,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      fetchRiskProcessFilterOptionsPage({
        page: Number(pageParam) || 1,
        pageSize: 20,
        search,
        riskId: trimmedRiskId,
        soxCycleCode: trimmedCycleCode,
      }),
    getNextPageParam: (lastPage) => {
      const items = lastPage.items;
      if (!Array.isArray(items) || items.length === 0) return undefined;
      const pageSize = lastPage.pageSize || 20;
      /** `isLastPage` from fetcher ignores bad `total_pages`; if missing, infer from short page. */
      if (lastPage.isLastPage === true) return undefined;
      if (lastPage.isLastPage == null && items.length < pageSize) return undefined;
      const page = typeof lastPage.page === 'number' && Number.isFinite(lastPage.page) ? lastPage.page : 1;
      return page + 1;
    },
  });
}

export function useRiskControlFilterOptions(
  search: string,
  classification = 'business',
  enabled = true
): UseInfiniteQueryResult<RiskControlFilterOptionsPageView, Error> {
  const resolvedClassification = String(classification ?? '').trim() || 'business';
  return useInfiniteQuery({
    queryKey: [...queryKeys.risks.controlFilterOptions(resolvedClassification), search],
    enabled,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      fetchRiskControlFilterOptionsPage({
        page: Number(pageParam) || 1,
        pageSize: 20,
        search,
        classification: resolvedClassification,
      }),
    getNextPageParam: (lastPage) => {
      const items = lastPage.items;
      if (!Array.isArray(items) || items.length === 0) return undefined;
      const pageSize = lastPage.pageSize || 20;
      if (lastPage.isLastPage === true) return undefined;
      if (lastPage.isLastPage == null && items.length < pageSize) return undefined;
      const page = typeof lastPage.page === 'number' && Number.isFinite(lastPage.page) ? lastPage.page : 1;
      return page + 1;
    },
  });
}

export function useRisksDashboardMetrics(): UseQueryResult<RisksDashboardMetricsView, Error> {
  return useQuery({
    queryKey: queryKeys.risks.dashboard(),
    queryFn: fetchRisksDashboardMetrics,
  });
}
